/**
 * Image Grid Component
 * Displays library images in a responsive masonry-style grid
 * 
 * Requirements: 2.3, 2.4, 2.5, 7.4
 */

import { useCallback, useMemo } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import type { ImageLibraryItem } from '../api/imageLibraryApi';

interface ImageGridProps {
  images: ImageLibraryItem[];
  selectedId: string | null;
  onSelect: (image: ImageLibraryItem) => void;
  onDoubleClick: (image: ImageLibraryItem) => void;
  isLoading: boolean;
  isInitialLoad: boolean;
  onCreateWithAI?: () => void;
}

export function ImageGrid({
  images,
  selectedId,
  onSelect,
  onDoubleClick,
  isLoading,
  isInitialLoad,
  onCreateWithAI,
}: ImageGridProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);

  const handleClick = useCallback(
    (image: ImageLibraryItem) => {
      onSelect(image);
    },
    [onSelect]
  );

  const handleDoubleClick = useCallback(
    (image: ImageLibraryItem) => {
      onDoubleClick(image);
    },
    [onDoubleClick]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, image: ImageLibraryItem) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        // Enter triggers double-click behavior (immediate insertion)
        onDoubleClick(image);
      } else if (e.key === ' ') {
        e.preventDefault();
        // Space triggers selection
        onSelect(image);
      }
    },
    [onSelect, onDoubleClick]
  );

  if (isLoading && isInitialLoad) {
    // Show welcome state on initial load instead of skeleton placeholders
    return (
      <div style={styles.welcomeContainer} role="status" aria-label="Getting started">
        <div style={styles.welcomeIcon} aria-hidden="true">🖼️</div>
        <h4 style={styles.welcomeTitle}>Image Library</h4>
        <p style={styles.welcomeText}>
          Search saved course images above, then reuse them across lessons.
        </p>
        {onCreateWithAI && (
          <button
            type="button"
            style={styles.welcomeButton}
            onClick={onCreateWithAI}
          >
            Create an Image with AI
          </button>
        )}
      </div>
    );
  }

  if (isLoading) {
    // Every subsequent request uses a skeleton, including after empty results.
    return (
      <div style={styles.grid} aria-busy="true" aria-label="Loading images">
        {Array.from({ length: 8 }).map((_, index) => (
          <div key={index} style={styles.skeletonItem} aria-hidden="true">
            <div style={styles.skeleton} />
          </div>
        ))}
      </div>
    );
  }

  if (images.length === 0) {
    return null;
  }

  return (
    <div
      style={styles.grid}
      role="listbox"
      aria-label="Image library - select an image"
      aria-multiselectable="false"
    >
      {images.map((image) => {
        const isSelected = image.id === selectedId;
        const displayLabel = image.label?.trim() || image.originalFilename;
        return (
          <div
            key={image.id}
            role="option"
            aria-selected={isSelected}
            aria-label={`${displayLabel}${isSelected ? ', selected' : ''}`}
            tabIndex={0}
            style={{
              ...styles.gridItem,
              ...(isSelected ? styles.gridItemSelected : {}),
            }}
            onClick={() => handleClick(image)}
            onDoubleClick={() => handleDoubleClick(image)}
            onKeyDown={(e) => handleKeyDown(e, image)}
          >
            <div style={styles.thumbnailWrapper}>
              <img
                src={image.publicUrl}
                alt={`Thumbnail of ${displayLabel}`}
                style={styles.thumbnail}
                loading="lazy"
              />
              {isSelected && (
                <div style={styles.checkmark} aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="8" fill={tokens.ui.selectionBorder} />
                    <path d="M4.5 8L7 10.5L11.5 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
            </div>
            <div style={styles.imageName} title={image.originalFilename}>
              {image.originalFilename}
            </div>
            {image.label && (
              <div style={styles.imageLabel} title={image.label}>
                {image.label}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function createStyles(tokens: ReturnType<typeof useTheme>['tokens']): Record<string, React.CSSProperties> {
  return {
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
      gap: '12px',
      padding: '4px',
    },
    gridItem: {
      display: 'flex',
      flexDirection: 'column',
      borderRadius: '8px',
      overflow: 'hidden',
      cursor: 'pointer',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'transparent',
      backgroundColor: tokens.backgrounds.page,
      transition: `border-color ${tokens.ui.transitionDefault}, box-shadow ${tokens.ui.transitionDefault}`,
      outline: 'none',
      minHeight: '120px',
    },
    gridItemSelected: {
      borderColor: tokens.ui.selectionBorder,
      boxShadow: `0 0 0 2px ${tokens.ui.selectionBorder}33`,
    },
    thumbnailWrapper: {
      position: 'relative',
    },
    thumbnail: {
      width: '100%',
      height: '100px',
      objectFit: 'cover',
      backgroundColor: tokens.text.subtle,
      display: 'block',
    },
    checkmark: {
      position: 'absolute',
      top: '6px',
      right: '6px',
    },
    placeholderThumb: {
      width: '100%',
      height: '100px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: tokens.text.subtle,
    },
    placeholderIcon: {
      fontSize: '32px',
      opacity: 0.5,
    },
    imageName: {
      padding: '8px 8px 2px 8px',
      fontSize: '12px',
      color: tokens.text.muted,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    imageLabel: {
      padding: '0 8px 8px 8px',
      fontSize: '11px',
      color: tokens.text.subtle,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    skeletonItem: {
      borderRadius: '8px',
      overflow: 'hidden',
      minHeight: '120px',
    },
    skeleton: {
      width: '100%',
      height: '100%',
      minHeight: '120px',
      backgroundColor: tokens.text.subtle,
      animation: 'pulse 1.5s ease-in-out infinite',
      background: `linear-gradient(90deg, ${tokens.text.subtle} 25%, ${tokens.backgrounds.page} 50%, ${tokens.text.subtle} 75%)`,
      backgroundSize: '200% 100%',
    },
    welcomeContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px 24px',
      textAlign: 'center',
    },
    welcomeIcon: {
      fontSize: '48px',
      marginBottom: '16px',
    },
    welcomeTitle: {
      margin: '0 0 8px 0',
      fontSize: '18px',
      fontWeight: 600,
      color: tokens.text.primary,
    },
    welcomeText: {
      margin: '0 0 24px 0',
      fontSize: '14px',
      color: tokens.text.muted,
      maxWidth: '300px',
      lineHeight: 1.5,
    },
    welcomeButton: {
      padding: '10px 20px',
      fontSize: '14px',
      fontWeight: 500,
      backgroundColor: tokens.ui.selectionBorder,
      color: tokens.text.onBrand,
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
    },
  };
}
